/**
 * QuickSight Service
 * Handles QuickSight dashboard embedding and API calls
 */

import { AuthService } from './auth.service';
import { apiConfig } from '@/config/api.config';
import { API_ENVIRONMENTS } from '@/config/constant'

export interface QuickSightEmbedUrlRequest {
  dashboardId: string;
  resetDisabled?: boolean;
  undoRedoDisabled?: boolean;
}

export interface QuickSightEmbedUrlResponse {
  embedUrl: string;
  dashboardId: string;
}

export class QuickSightService {
  /**
   * Get QuickSight dashboard embed URL from API Gateway
   * Uses query parameter for dashboard ID (simpler and RESTful)
   */
  static async getEmbedUrl(
    dashboardId: string,
    options?: Partial<QuickSightEmbedUrlRequest & { paramString?: string }>
  ): Promise<string> {
    try {
      // Get the Cognito access token (recommended for API Gateway authorizer)
      const accessToken = await AuthService.getIdToken();

      // Use relative /api path for local development (proxy), otherwise use full URL
      let url;
      if (process.env.NODE_ENV === API_ENVIRONMENTS.DEV) {
        url = new URL(`/api${apiConfig.endpoints.getQuickSightEmbedUrl}`, window.location.origin);
      } else {
        url = new URL(`${apiConfig.baseUrl}${apiConfig.endpoints.getQuickSightEmbedUrl}`);
      }
      url.searchParams.append('dashboardId', dashboardId);
      // If paramString is provided, add measures as a query param
      if (options && options.paramString) {
        // paramString is like '?measures=val1,val2', so parse and append
        const params = new URLSearchParams(options.paramString.replace(/^\?/, ''));
        for (const [key, value] of params.entries()) {
          url.searchParams.append(key, value);
        }
      }

      // Send Authorization header for Cognito User Pool authorizer
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to get embed URL: ${response.statusText}`
        );
      }

      const data: QuickSightEmbedUrlResponse = await response.json();
      return data.embedUrl;
    } catch (error) {
      console.error('Error getting QuickSight embed URL:', error);
      throw error;
    }
  }

  /**
   * Get list of available dashboards for the user
   */
  static async getUserDashboards() {
    try {
      const idToken = await AuthService.getIdToken();
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }


      let url;
      if (process.env.NODE_ENV === API_ENVIRONMENTS.DEV) {
        url = `/api${apiConfig.endpoints.getUserDashboards}`;
      } else {
        url = `${apiConfig.baseUrl}${apiConfig.endpoints.getUserDashboards}`;
      }

      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get dashboards: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user dashboards:', error);
      throw error;
    }
  }
}

export default QuickSightService;
